import type { AggResponse, SearchPageNode } from '@atlassian/search-client';

export type ValidationResult =
	| { success: true }
	| {
			success: false;
			error: {
				name: string;
				message: string;
			};
		};

const createValidationError = (message: string): ValidationResult => {
	return {
		success: false,
		error: {
			name: 'ValidationError',
			message,
		},
	};
};

const validateBaseSearchResultsResponse = (response: AggResponse<unknown>): ValidationResult => {
	if (!response) {
		return createValidationError('Response is null or undefined');
	}

	// GraphQL response should always include a `data` key (it can be null)
	if (!('data' in response)) {
		return createValidationError('Response missing "data" field');
	}

	if (!response.data) {
		return { success: true };
	}

	const data: any = response.data;
	if (!data.search) {
		return createValidationError('Response missing "data.search" field');
	}

	if (!data.search.results) {
		return createValidationError('Response missing "data.search.results" field');
	}

	if (!('edges' in data.search.results)) {
		return createValidationError('Response missing "data.search.results.edges" field');
	}

	if (!Array.isArray(data.search.results.edges)) {
		return createValidationError('Expected "data.search.results.edges" to be an array');
	}

	if (response.extensions !== undefined && response.extensions !== null) {
		if (typeof response.extensions !== 'object') {
			return createValidationError('Expected "extensions" to be an object');
		}
	}

	if (response.errors !== undefined && response.errors !== null) {
		if (!Array.isArray(response.errors)) {
			return createValidationError('Expected "errors" to be an array');
		}
	}

	return { success: true };
};

const validateEdgesHaveRenderableNodes = (response: AggResponse<unknown>, productName: string): ValidationResult => {
	const baseResult = validateBaseSearchResultsResponse(response);
	if (!baseResult.success) {
		return baseResult;
	}

	if (!response.data) {
		return { success: true };
	}

	const edges: any[] | undefined = (response.data as any).search?.results?.edges;
	if (!edges || edges.length === 0) {
		return { success: true };
	}

	for (let i = 0; i < edges.length; i++) {
		const node: SearchPageNode | null | undefined = edges[i]?.node;
		if (!node) {
			return createValidationError(`${productName} response has null/undefined node at index ${i}`);
		}

		if (!node.id || typeof node.id !== 'string' || node.id.length === 0) {
			return createValidationError(`${productName} response node at index ${i} missing valid 'id'`);
		}

		// title can be empty string but not null/undefined
		if (node.title === null || node.title === undefined) {
			return createValidationError(`${productName} response node at index ${i} missing 'title'`);
		}
	}

	return { success: true };
};

export const validateConfluenceEntitiesResponse = (response: AggResponse<unknown>): ValidationResult => {
	return validateEdgesHaveRenderableNodes(response, 'Confluence entities');
};

export const validateConfluenceSpacesResponse = (response: AggResponse<unknown>): ValidationResult => {
	return validateEdgesHaveRenderableNodes(response, 'Confluence spaces');
};

export const validateJiraIssuesResponse = (response: AggResponse<unknown>): ValidationResult => {
	return validateEdgesHaveRenderableNodes(response, 'Jira issues');
};

export const validateJiraEntitiesResponse = (response: AggResponse<unknown>): ValidationResult => {
	return validateEdgesHaveRenderableNodes(response, 'Jira entities');
};

export const validateAtlasResponse = (response: AggResponse<unknown>): ValidationResult => {
	return validateEdgesHaveRenderableNodes(response, 'Atlas');
};

export const validateMercuryResponse = (response: AggResponse<unknown>): ValidationResult => {
	return validateEdgesHaveRenderableNodes(response, 'Mercury');
};

export const validateTalentResponse = (response: AggResponse<unknown>): ValidationResult => {
	return validateEdgesHaveRenderableNodes(response, 'Talent');
};

export const validateAssetsResponse = (response: AggResponse<unknown>): ValidationResult => {
	return validateEdgesHaveRenderableNodes(response, 'Assets');
};
